<template>
    <div class="search-widget-container hotel-search">
        <div class="flex-items" v-if="!iframeWidget">
            <div class="title">
                <span v-if="!$route.params.destinationType">{{ $t('searchHotels') }}</span>
                <span class="location" v-if="$route.params.destinationType">
                    {{ $route.params.destinationType.toLowerCase() === 'city' ? $t('hotelsIn') : $route.params.destinationType.toLowerCase() === 'airport' ? $t('hotelsNear') : 'Hotels' }}
                    {{ searchQuery.destination.label }}
                    {{ searchQuery.destination.state ? `, ${searchQuery.destination.state}` : '' }}
                </span>
            </div>

            <div class="dates" v-if="searchQuery.checkIn">
                <span>{{ searchQuery.checkIn | moment("ddd, MMM Do") }} - {{ searchQuery.checkOut | moment("ddd, MMM Do") }}</span>
            </div>

            <div class="toggle"
                v-if="showSearchToggle" 
                @click="showSearchWidget = !showSearchWidget"
                :title="$t('editSearch')">
                <span v-if="!showSearchWidget">{{ $t('editSearch') }} <i class="fal fa-search"></i></span>
                <span v-else>{{ $t('hideSearch') }} <i class="fal fa-times"></i></span> 
            </div>
            
        </div>
        <div class="search-widget" v-if="showSearchWidget">

            <h2 v-if="iframeWidget">{{ $t('searchHotels') }}</h2>

            <div class="form-fields">
                <div class="destination">
                    <div class="form-group">
                        <v-select
                            v-model="searchQuery.destination"
                            @search="searchDestinationLocation"
                            :options="destinationResults"
                            :placeholder="$t('enterACityOrAirport') + '...'"
                            label="label"
                            :filterable="false"
                            >
                            <template slot="no-options">
                                {{ $t('enterACityOrAirport') }}...
                            </template>
                            <template slot="option" slot-scope="option">
                                <div class="autocomplete-result">
                                    <i class="fal fa-city" v-if="option.type === 'city'"></i>
                                    <i class="fal fa-plane" v-if="option.type === 'airport'"></i>
                                    <i class="fal fa-hotel" v-if="option.type === 'hotel'"></i>
                                    {{ option.label + ( option.state ? `, ${option.state}` : '' ) + (option.country ? `, ${option.country}` : '') }}
                                </div>
                            </template>
                            <template slot="selected-option" slot-scope="option">
                                <div class="autocomplete-result selected">
                                    <i class="fal fa-city" v-if="option.type === 'city'"></i>
                                    <i class="fal fa-plane" v-if="option.type === 'airport'"></i>
                                    <i class="fal fa-hotel" v-if="option.type === 'hotel'"></i>
                                    {{ option.label + ( option.state ? `, ${option.state}` : '' ) + (option.country ? `, ${option.country}` : '') }}
                                </div>
                            </template>
                        </v-select>
                            
                    </div>
                </div>
                
                <div class="dates">
                    <div class="form-group round-trip">
                        <date-picker
                                mode="range"
                                v-model="searchQuery.roundTripDates"
                                :input-props='{
                                    class: "date-picker",
                                    placeholder: $t("checkInAndCheckoutDates"),
                                    readonly: true
                                }'
                                :popover='{
                                    visibility: "focus",
                                    placement: "bottom-start"
                                }'
                                :min-date="new Date()"
                                :max-date="maxDate"
                                value="null">
                        </date-picker>

                        <i class="fal fa-calendar"></i>
                    </div>
                </div>

                <div class="rooms-guests" v-click-outside="hidePaxToggles">
                    <div class="form-group">
                        <input 
                            type="text" 
                            :value="searchQuery.rooms + ' ' + (searchQuery.rooms > 1 ? $t('rooms') : $t('room')) + ', ' + searchQuery.adults + ' ' + (searchQuery.adults > 1 ? $t('adults') : $t('adult')) + (searchQuery.children > 0 ? (', ' + searchQuery.children + ' ' + (searchQuery.children > 1 ? $t('children') : $t('children'))) : '')"
                            @click="showRoomsGuests = !showRoomsGuests" 
                            />
                    </div>
                    <div class="passenger-toggles" v-if="showRoomsGuests">
                        <div class="overlay"></div>

                        <div class="passenger-toggle">
                            <div class="pax-label">
                                {{ $t('rooms') }}
                            </div>
                            <div class="toggles">
                                <button class="btn btn-sec btn-sm" @click="searchQuery.rooms > 1 ? searchQuery.rooms-- : null"><i class="fal fa-minus"></i></button>
                                <input type="text" v-model="searchQuery.rooms" class="count" />
                                <button class="btn btn-sec btn-sm" @click="searchQuery.rooms < 9 ? searchQuery.rooms++ : null"><i class="fal fa-plus"></i></button>
                            </div>
                        </div>

                        <div class="passenger-toggle">
                            <div class="pax-label">
                                {{ $t('adults') }}
                                <span>{{ $t('over', {number: '16'}) }}</span>
                            </div>
                            <div class="toggles">
                                <button class="btn btn-sec btn-sm" @click="searchQuery.adults > 1 ? searchQuery.adults-- : null"><i class="fal fa-minus"></i></button>
                                <input type="text" v-model="searchQuery.adults" class="count" />
                                <button class="btn btn-sec btn-sm" @click="searchQuery.adults < 10 ? searchQuery.adults++ : null"><i class="fal fa-plus"></i></button>
                            </div>
                        </div>

                        <div class="passenger-toggle">
                            <div class="pax-label">
                                {{ $t('children') }}
                                <span>0 - 16</span>
                            </div>
                            <div class="toggles">
                                <button class="btn btn-sec btn-sm" @click="searchQuery.children > 0 ? searchQuery.children-- : null"><i class="fal fa-minus"></i></button>
                                <input type="text" v-model="searchQuery.children" class="count" />
                                <button class="btn btn-sec btn-sm" @click="searchQuery.children < 10 ? searchQuery.children++ : null"><i class="fal fa-plus"></i></button>
                            </div>
                        </div>
                    </div>
                </div>


                <button class="btn btn-search" @click="executeSearch()">{{ $t('searchHotels') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import HotelsApi from "../../services/hotelsApi";
import { searchWidgetMixin } from '@/mixins/searchWidgetMixin'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
Vue.component('date-picker', DatePicker)
import 'vue-datetime/dist/vue-datetime.css'
import {mapGetters} from "vuex";

export default {
    data () {
      return {
        api: null,
        moment: this.$moment,
        destinationResults: [],
        showRoomsGuests: false,
        daysBetween: null,
        searchQuery: {
            destination: '',
            checkIn: '',
            checkOut: '',
            roundTripDates: {
                start: '',
                end: ''
            },
            rooms: 1,
            adults: 2,
            children: 0
          },
        }
    },
    props: {
        showSearchWidget: Boolean,
        showSearchToggle: Boolean,
        iframeWidget: Boolean
    },
    mixins: [searchWidgetMixin],  

    mounted() {
      this.api = new HotelsApi();
      this.setupParams();
    },

    methods: {
        setupParams: function () {
            let self = this;
            let params = this.$route.params;

            if (params.hasOwnProperty('destinationName') && params.destinationName && params.hasOwnProperty('destination') && params.destination) {
            self.searchForDestinationUsingApi(params.destinationName.split('-').join(' ')).then((results) => {
                results = results.getHotelAutoSuggestV2.results.result;
                let selectedItem = {};
                switch (params.destinationType.toLowerCase()) {
                case 'airport':
                    selectedItem = results.airports.find(item => item.iata === params.destination);
                    self.searchQuery.destination = {
                        rank: selectedItem.rank,
                        label: selectedItem.airport,
                        value: selectedItem.iata,
                        state: selectedItem.state_code,
                        country: selectedItem.country_code,
                        type: selectedItem.type
                    };
                    break;
                case 'hotel':
                    selectedItem = results.hotels.find(item => item.hotelid_ppn === params.destination);
                    self.searchQuery.destination = {
                        rank: selectedItem.rank,
                        label: selectedItem.hotel_name,
                        value: selectedItem.hotelid_ppn,
                        state: selectedItem.address.state_code,
                        country: selectedItem.address.country_code,
                        type: selectedItem.type
                    }
                    break;
                case 'city':
                    selectedItem = results.cities.find(item => item.cityid_ppn === params.destination);
                    self.searchQuery.destination = {
                        rank: selectedItem.rank,
                        label: selectedItem.city,
                        value: selectedItem.cityid_ppn,
                        state: selectedItem.state_code,
                        country: selectedItem.country_code,
                        type: selectedItem.type
                    };
                    break;
                }
            })
            }

            if (params.hasOwnProperty('checkIn') && params.checkIn && params.hasOwnProperty('checkOut') && params.checkOut ) {
                self.searchQuery.checkIn = self.$moment(params.checkIn, 'YYYY-MM-DD').toDate();
                self.searchQuery.checkOut = self.$moment(params.checkOut, 'YYYY-MM-DD').toDate();
                self.searchQuery.roundTripDates.start = self.searchQuery.checkIn;
                self.searchQuery.roundTripDates.end = self.searchQuery.checkOut;
            }

            let queryParams = self.$route.query;

            if (queryParams.hasOwnProperty('rooms')) {
                let count = parseInt(queryParams.rooms);
                if (!isNaN(count)) {
                    self.searchQuery.rooms = count;
                }
            }
            if (queryParams.hasOwnProperty('adults')) {
                let count = parseInt(queryParams.adults);
                if (!isNaN(count)) {
                    self.searchQuery.adults = count;
                }
            }
            if (queryParams.hasOwnProperty('children')) {
                let count = parseInt(queryParams.children);
                if (!isNaN(count)) {
                    self.searchQuery.children = count;
                }
            }
        },

        hidePaxToggles: function() {
            this.showRoomsGuests = false
        },

        clearRoomsGuests: function() {
            this.searchQuery.rooms = 1;
            this.searchQuery.adults = 2;
            this.searchQuery.children = 0;
        },

        searchDestinationLocation: function(search, loading) {
            let self = this;
            if (self.timeout) clearTimeout(self.timeout);
            self.timeout = setTimeout(() => {
                if(search != '') {
                    loading(true);
                    self.searchForDestinationUsingApi(search)
                    .then(function (results) {
                        results = results.getHotelAutoSuggestV2.results.result;
                        self.destinationResults = [];
                        if (results.cities) {
                            self.destinationResults = self.destinationResults.concat(results.cities.map(item => {
                            return {rank: item.rank, label: item.city, value: item.cityid_ppn, state: item.state_code, country: item.country_code, type: item.type};
                            }));
                        }
                        if (results.airports) {
                            self.destinationResults = self.destinationResults.concat(results.airports.map(item => {
                            return {rank: item.rank, label: item.airport, value: item.iata, state: item.state_code, country: item.country_code, type: item.type}
                            }));
                        }
                        if (results.hotels) {
                            self.destinationResults = self.destinationResults.concat(results.hotels.map(item => {
                            return {rank: item.rank, label: item.hotel_name, value: item.hotelid_ppn, state: item.address.state_code, country: item.address.country_code, type: item.type}
                            }));
                        }
                        loading(false);
                    })
                    .catch(() => { });
                }
          }, 500);
        },

        searchForDestinationUsingApi: function(search) {
            let self = this;
            return new Promise((resolve, reject) => {
                self.api.suggest(window.TravelPlatform.key, search)
                .then(results => { resolve(results.data); })
                .catch(ex => { reject(ex); });
            });
        },

        executeSearch: function() {
            let self = this;
            let locale = self.$i18n.locale;
            let route = `/${locale}/hotels/search`;

            let checkIn = self.$moment(self.searchQuery.roundTripDates.start);
            let checkOut = self.$moment(self.searchQuery.roundTripDates.end);
            self.daysBetween = parseInt(checkOut.diff(checkIn, 'days'));

            if(self.daysBetween <= 21) {
                self.$validator.validateAll().then((result) => {
                    route += '/' + self.getCurrencyCode;
                    route += '/' + `${self.searchQuery.destination.type}-${self.searchQuery.destination.label.split(' ').join('-').split(',').join('-')}${self.searchQuery.destination.state ? `-${self.searchQuery.destination.state}` : ''}${self.searchQuery.destination.country ? `-${self.searchQuery.destination.country}` : ''}`;
                    route += '/' + self.searchQuery.destination.value;

                    route += '/' + self.$moment(self.searchQuery.roundTripDates.start).format("YYYY-MM-DD");
                    route += '/' + self.$moment(self.searchQuery.roundTripDates.end).format("YYYY-MM-DD");


                    route += '?rooms=' + self.searchQuery.rooms;
                    route += '&adults=' + self.searchQuery.adults;
                    route += '&children=' + self.searchQuery.children;
                    if(self.getAgencyDisplay.products.hotels.sortOrder) {
                        route += '&sort=' + self.getAgencyDisplay.products.hotels.sortOrder
                    } else {
                        route += '&sort=gr'
                    }

                    if (result) {
                        self.searchQuery.currency = self.getCurrencyCode;
                        self.$store.dispatch('saveHotelSearchCriteria', self.searchQuery);

                        if(self.iframeWidget) {
                            top.window.location.href = route;
                        } else {
                            self.$router.push(route);
                        }
                    }
                });
            } else {
                self.$notify({
                    group: 'saved',
                    title: 'Hotels stays cannot be longer than 21 days. Please adjust your travel dates.',
                    type: 'error'
                })
            }
        },

    },
    computed: {
      ...mapGetters([
        'getCurrencyCode',
        'getAgencyDisplay'
      ]),

      maxDate() {
        let self = this;
        let date = new Date();
        date = self.$moment(new Date()).add(330, 'days');
        date = self.$moment(date).format("MM-DD-YYYY HH:mm");

        return date;
      }
    },
}
</script>